import {
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnDestroy,
  Output,
  ViewChild,
} from '@angular/core';
import { BrowserMultiFormatReader, IScannerControls } from '@zxing/browser';

import { BaseUserModel } from '../../../../models/baseUser.model';
import { ScanProduct } from '../../../../models/scan-product.model';
import { ErrorHandlerService } from '../../../../services/error-handler.service';
import { ScanService } from '../../../../services/scan.service';
import { ToasterService } from '../../../../services/toaster.service';
import { UserService } from '../../../../services/user.service';
import { ProductLiteResponseModel } from '../../../../../modules/products-catalog/models/response-models/product-response.model';

@Component({
  selector: 'app-work-list-item-entry',
  standalone: false,
  templateUrl: './work-list-item-entry.component.html',
  styleUrl: './work-list-item-entry.component.css',
})
export class WorkListItemEntryComponent implements OnDestroy {
  constructor(
    private scanProductService: ScanService,
    private userService: UserService,
    private readonly toaster: ToasterService,
    private errorHandlerService: ErrorHandlerService,
  ) {}

  userDetails: BaseUserModel = new BaseUserModel();
  verifyProduct: ScanProduct = new ScanProduct();
  @ViewChild('video') videoElementRef!: ElementRef<HTMLVideoElement>;

  // Input and Output properties for quantity and product
  @Input() quantity: number = 0;
  @Input() product: ProductLiteResponseModel = new ProductLiteResponseModel();
  @Output() quantityChanged = new EventEmitter<number>();
  onQuantityChange() {
    if (this.quantity < 1) {
      this.toaster.error('Quantity must be at least 1');
      return;
    }
    this.quantityChanged.emit(this.quantity);
  }

  preventNegative(event: KeyboardEvent) {
    if (event.key === '-' || event.key === 'e' || event.key === '+') {
      event.preventDefault();
    }
  }

  async ngOnInit() {
    const user = await this.userService.getUserFromLocalStorage(); // Assuming companyId is a property of user
    if (user) {
      this.userDetails = user;
    } else {
      // Handle the case where user is null
      this.toaster.error('User details not found');
    }
  }
  // Barcode scanner variables and methods
  result: string | null = null;
  scanning = false;

  private readonly codeReader = new BrowserMultiFormatReader();
  private stream: MediaStream | null = null;
  private videoElement: HTMLVideoElement | null = null;
  private controls: IScannerControls | null = null;

  async toggleScan() {
    if (this.scanning) {
      this.stopScan();
    } else {
      this.scanning = true;
      setTimeout(() => {
        this.startScan();
      });
    }
  }

  private async startScan() {
    this.videoElement = this.videoElementRef.nativeElement;
    this.result = null;

    try {
      this.stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: 'environment' },
      });

      this.videoElement.srcObject = this.stream;

      // safer playback
      await this.videoElement.play().catch(() => {});

      this.controls = await this.codeReader.decodeFromVideoDevice(
        undefined,
        this.videoElement,
        (result, err) => {
          if (result) {
            this.result = result.getText();
            if (this.result === this.product.sku) {
              this.verifyProduct.companyId = this.userDetails.companyId;
              this.verifyProduct.sku = this.product.sku;
              this.verifyProduct.name = this.product.name;
              this.verifyProduct.batchNumber = '';
              this.verifyProduct.serial = '';
              this.scanProductService.ValidateScanProduct(this.verifyProduct).subscribe(
                () => {
                  this.quantity = this.quantity + 1;
                },
                (err) => {
                  this.errorHandlerService.handleApiError(err);
                },
              );
              this.onQuantityChange();
              this.result = null; // reset result to allow further scans
            }
            this.stopScan();
          }
        },
      );
    } catch (error) {
      this.errorHandlerService.handleApiError(error);
      this.scanning = false;
    }
  }

  private stopScan() {
    if (this.controls) {
      this.controls.stop();
      this.controls = null;
    }

    if (this.stream) {
      this.stream.getTracks().forEach((track) => track.stop());
      this.stream = null;
    }

    if (this.videoElement) {
      this.videoElement.style.display = 'none';
    }

    this.scanning = false;
  }

  ngOnDestroy() {
    this.stopScan();
  }
}
